import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  TrendingUp, 
  ArrowLeft, 
  Search, 
  Plus,
  Package,
  Check,
  X,
  Loader2,
  PackagePlus,
  ChevronsUpDown,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Tag,
  Filter,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DualPrice from "@/components/DualPrice";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import QuickOrderDialog from "@/components/purchasing/QuickOrderDialog";
import { Edit2, Save, Beaker, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact price display component with expand functionality
function CompactPriceDisplay({ product, suppliers, exchangeRate, onEditPrice }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Convert price to SEK
  const convertToSEK = (price, currency) => {
    if (currency === "EUR") {
      return Math.round(price * exchangeRate);
    }
    return Math.round(price);
  };
  
  // Get all prices with supplier info
  const allEntries = suppliers
    .map(supplier => {
      const priceInfo = product.prices[supplier.id];
      if (!priceInfo) return null;
      return {
        supplierId: supplier.id,
        supplierName: supplier.company_name,
        price: priceInfo.purchase_price,
        currency: priceInfo.currency || "SEK",
        priceSEK: convertToSEK(priceInfo.purchase_price, priceInfo.currency || "SEK"),
        chemicalTax: priceInfo.chemical_tax || 0,
        unavailable: !!priceInfo.unavailable,
        unavailableNote: priceInfo.unavailable_note || ""
      };
    })
    .filter(Boolean);

  const unavailableEntries = allEntries.filter(p => p.unavailable);
  const pricesWithSuppliers = allEntries
    .filter(p => !p.unavailable && p.priceSEK > 0)
    .sort((a, b) => a.priceSEK - b.priceSEK);

  const priceCount = pricesWithSuppliers.length;
  const lowestPrice = pricesWithSuppliers[0];
  
  if (priceCount === 0) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {unavailableEntries.length > 0 ? (
          unavailableEntries.map(p => (
            <Badge key={p.supplierId} variant="outline" className="text-red-600 border-red-500/50 bg-red-500/5 text-xs gap-1"
              title={p.unavailableNote || "Leverantören får ej tag på produkten"}>
              <X className="h-3 w-3" />{p.supplierName}: Otillgänglig
            </Badge>
          ))
        ) : (
          <span className="text-muted-foreground text-sm">Inget pris</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onEditPrice(product)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Lägg till
        </Button>
      </div>
    );
  }
  
  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Lowest price */}
        <div className="flex items-center gap-2">
          <span className="font-bold text-green-500 text-lg">
            {lowestPrice.priceSEK.toLocaleString('sv-SE')} kr
          </span>
          <span className="text-xs text-muted-foreground">
            ({lowestPrice.supplierName})
          </span>
        </div>
        
        {/* Price count badge - clickable if more than 1 */}
        {priceCount > 1 ? (
          <CollapsibleTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-6 text-xs gap-1 px-2"
              data-testid={`expand-prices-${product.ean}`}
            >
              <Tag className="h-3 w-3" />
              {priceCount} priser
              {isExpanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
        ) : (
          <Badge variant="secondary" className="text-xs">
            1 pris
          </Badge>
        )}

        {/* Unavailable suppliers indicator */}
        {unavailableEntries.map(p => (
          <Badge key={p.supplierId} variant="outline" className="text-red-600 border-red-500/50 bg-red-500/5 text-xs gap-1"
            title={p.unavailableNote || "Leverantören får ej tag på produkten"}>
            <X className="h-3 w-3" />{p.supplierName}: Otillgänglig
          </Badge>
        ))}
      </div>
      
      {/* Expanded view showing all prices */}
      <CollapsibleContent className="mt-2">
        <div className="bg-muted/30 rounded-lg p-2 space-y-1">
          {pricesWithSuppliers.map((p, idx) => (
            <div 
              key={p.supplierId}
              className={cn(
                "flex items-center justify-between py-1 px-2 rounded text-sm",
                idx === 0 && "bg-green-500/10"
              )}
            >
              <span className="text-muted-foreground">{p.supplierName}</span>
              <div className="flex items-center gap-3">
                <span className={cn(
                  "font-medium",
                  idx === 0 ? "text-green-500" : ""
                )}>
                  {p.priceSEK.toLocaleString('sv-SE')} kr
                </span>
                {p.currency === "EUR" && (
                  <span className="text-xs text-muted-foreground">
                    ({p.price} €)
                  </span>
                )}
                {idx === 0 && (
                  <Badge variant="success" className="text-xs">Lägst</Badge>
                )}
              </div>
            </div>
          ))}
          {unavailableEntries.map(p => (
            <div key={p.supplierId} className="flex items-center justify-between py-1 px-2 rounded text-sm bg-red-500/5">
              <span className="text-red-600">{p.supplierName}</span>
              <span className="text-xs text-red-600 flex items-center gap-1" title={p.unavailableNote}>
                <X className="h-3 w-3" />Får ej tag på produkten
              </span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Inline editable cell for chemical tax
function ChemicalTaxCell({ product, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  // Get chemical_tax from any supplier price (it's per product, not per supplier)
  const existingTax = Object.values(product.prices || {}).find(p => p?.chemical_tax)?.chemical_tax || 0;
  const [tax, setTax] = useState(existingTax.toString());
  
  const handleSave = async () => {
    await onSave(parseFloat(tax) || 0);
    setIsEditing(false);
  };
  
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          type="number"
          step="0.01"
          value={tax}
          onChange={(e) => setTax(e.target.value)}
          className="w-16 h-7 text-xs text-right"
          placeholder="0"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setIsEditing(false);
          }}
        />
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleSave}>
          <Save className="h-3 w-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setIsEditing(false)}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }
  
  return (
    <div 
      className="flex items-center gap-1 cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 group"
      onClick={() => setIsEditing(true)}
    >
      {existingTax > 0 ? (
        <span className="text-xs font-medium">{existingTax} kr</span>
      ) : (
        <span className="text-xs text-muted-foreground">-</span>
      )}
      <Edit2 className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

// Inline editable row for supplier price
function SupplierPriceRow({ supplier, existingPrice, onSave, onDelete, savingPrice, exchangeRate = 11.50 }) {
  const isNew = !existingPrice?.id && existingPrice?.purchase_price === 0;
  const [isEditing, setIsEditing] = useState(isNew);
  const [price, setPrice] = useState(isNew ? "" : (existingPrice?.purchase_price?.toString() || ""));
  const [currency, setCurrency] = useState(existingPrice?.currency || supplier.default_currency || "SEK");
  const [deleting, setDeleting] = useState(false);
  
  const handleSave = async () => {
    if (!price) return;
    await onSave(price, currency);
    setIsEditing(false);
  };
  
  const handleDelete = async () => {
    if (!existingPrice?.id) return;
    setDeleting(true);
    try {
      await onDelete(existingPrice.id);
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="font-medium">{supplier.company_name}</div>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <Input
              type="number"
              step="1"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-24 h-8 text-right"
              placeholder="Pris"
              autoFocus
            />
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SEK">SEK</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={savingPrice || !price}
            >
              <Save className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setPrice(existingPrice?.purchase_price?.toString() || "");
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </>
        ) : existingPrice ? (
          <>
            <DualPrice
              price={existingPrice.purchase_price}
              currency={existingPrice.currency}
              rate={exchangeRate}
              testId={`price-dual-${supplier.id}`}
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => setIsEditing(true)}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid={`delete-price-${supplier.id}`}
            >
              {deleting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsEditing(true)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Lägg till
          </Button>
        )}
      </div>
    </div>
  );
}

export default function PricesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [exchangeRate, setExchangeRate] = useState(11.50);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 50;
  
  // Filters
  const [filterBrand, setFilterBrand] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [filterSupplier, setFilterSupplier] = useState("");
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [brandFilterOpen, setBrandFilterOpen] = useState(false);
  const [categoryFilterOpen, setCategoryFilterOpen] = useState(false);
  const [supplierFilterOpen, setSupplierFilterOpen] = useState(false);
  
  // Saving state
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Add/Edit price dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedProductForEdit, setSelectedProductForEdit] = useState(null);
  const [addPriceForm, setAddPriceForm] = useState({
    supplier_id: "",
    purchase_price: "",
    currency: "SEK",
    ean: "",
    produktnamn: "",
    chemical_tax: "0"
  });
  
  // Product search for add dialog
  const [productSearchForAdd, setProductSearchForAdd] = useState("");
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingForAdd, setSearchingForAdd] = useState(false);
  
  // Create new product dialog
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [createProductForm, setCreateProductForm] = useState({
    artikelnummer: "",
    ean: "",
    kategori: "",
    tillverkare: ""
  });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  
  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importMatchBy, setImportMatchBy] = useState("ean");
  const [importSupplierId, setImportSupplierId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);
  
  // Order warning dialog state
  const [showOrderWarning, setShowOrderWarning] = useState(false);
  const [orderWarningProduct, setOrderWarningProduct] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);
  
  // Quick order dialog state
  const [showQuickOrderDialog, setShowQuickOrderDialog] = useState(false);
  const [quickOrderProduct, setQuickOrderProduct] = useState(null);
  
  // Combobox open states
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [manufacturerOpen, setManufacturerOpen] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);
  
  // Check for add parameter (product to pre-select)
  useEffect(() => {
    const addEan = searchParams.get('add');
    if (addEan && suppliers.length > 0) {
      // Pre-fetch the product and open dialog
      prefetchProductForAdd(addEan);
      // Clear the parameter
      searchParams.delete('add');
      setSearchParams(searchParams);
    }
  }, [suppliers, searchParams]);
  
  const prefetchProductForAdd = async (ean) => {
    try {
      const [productRes, pricesRes] = await Promise.all([
        axios.get(`${API}/inventory`, { params: { search: ean, limit: 1 } }),
        axios.get(`${API}/purchasing/prices/by-ean/${ean}`).catch(() => ({ data: [] }))
      ]);
      
      const products = productRes.data.items || [];
      if (products.length > 0) {
        const product = products[0];
        const prices = pricesRes.data || [];
        const priceWithTax = prices.find(p => p.chemical_tax);
        
        setAddPriceForm({
          supplier_id: suppliers[0]?.id || "",
          purchase_price: "",
          currency: "SEK",
          ean: product.ean,
          produktnamn: product.produktnamn || product.artikelnummer,
          chemical_tax: priceWithTax ? priceWithTax.chemical_tax.toString() : "0"
        });
        // Set product with empty prices object to avoid undefined errors
        setSelectedProductForEdit({
          ...product,
          prices: product.prices || {}
        });
        setShowAddDialog(true);
        toast.info(`Lägg till pris för ${product.artikelnummer}`);
      }
    } catch (error) {
      console.error("Error prefetching product:", error);
    }
  };
  
  // Fetch products when filters or page change
  useEffect(() => {
    if (!loading) {
      fetchProducts();
    }
  }, [currentPage, filterBrand, filterCategory, filterSupplier, searchQuery]);

  // Search products for add dialog
  useEffect(() => {
    if (productSearchForAdd.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearchingForAdd(true);
      try {
        const response = await axios.get(`${API}/inventory`, {
          params: { search: productSearchForAdd, limit: 10 }
        });
        setProductSearchResults(response.data.items || []);
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setSearchingForAdd(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [productSearchForAdd]);

  const fetchInitialData = async () => {
    try {
      const [suppliersRes, filterOptionsRes, categoriesRes, manufacturersRes, rateRes] = await Promise.all([
        axios.get(`${API}/purchasing/suppliers`),
        axios.get(`${API}/purchasing/prices/filter-options`),
        axios.get(`${API}/inventory/categories`),
        axios.get(`${API}/inventory/manufacturers`),
        axios.get(`${API}/purchasing/exchange-rate`)
      ]);
      setSuppliers(suppliersRes.data);
      setAvailableBrands(filterOptionsRes.data.brands || []);
      setAvailableCategories(filterOptionsRes.data.categories || []);
      setAvailableSuppliers(filterOptionsRes.data.suppliers || []);
      setCategories(categoriesRes.data.filter(c => c && c !== "Välj Kategori"));
      setManufacturers(manufacturersRes.data.filter(m => m && m !== "Välj Tillverkare"));
      setExchangeRate(rateRes.data.rate || 11.50);
      
      // Fetch first page of products
      await fetchProducts();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProducts = async () => {
    try {
      const params = {
        skip: currentPage * pageSize,
        limit: pageSize
      };
      if (searchQuery) params.search = searchQuery;
      if (filterBrand) params.brand = filterBrand;
      if (filterCategory) params.category = filterCategory;
      if (filterSupplier) params.supplier = filterSupplier;
      
      const productsRes = await axios.get(`${API}/purchasing/prices/all-products`, { params });
      setAllProducts(productsRes.data.products || []);
      setTotalProducts(productsRes.data.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };
  
  // Debounced search - triggers fetchProducts after delay
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchProducts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // No need for client-side filtering anymore - server does it
  const filteredProducts = allProducts;
  
  const totalPages = Math.ceil(totalProducts / pageSize);

  // Convert price to SEK
  const convertToSEK = (price, currency) => {
    if (currency === "EUR") {
      return Math.round(price * exchangeRate);
    }
    return Math.round(price);
  };

  // Save chemical tax for a product (updates all supplier prices for that EAN)
  const handleSaveChemicalTax = async (ean, newTax) => {
    try {
      // Find all price IDs for this EAN and update them
      const product = allProducts.find(p => p.ean === ean);
      if (!product) return;
      
      const priceIds = Object.values(product.prices || {})
        .filter(p => p?.id)
        .map(p => p.id);
      
      if (priceIds.length === 0) {
        toast.error("Ingen prislista att uppdatera. Lägg till pris först.");
        return;
      }
      
      // Update all prices for this product with the new chemical tax
      await Promise.all(
        priceIds.map(priceId => 
          axios.put(`${API}/purchasing/prices/${priceId}`, {
            chemical_tax: newTax
          })
        )
      );
      
      toast.success("Kem. skatt sparad");
      fetchInitialData(); // Refresh data
    } catch (error) {
      console.error("Error saving chemical tax:", error);
      toast.error("Kunde inte spara kem. skatt");
    }
  };

  // Open edit dialog for a specific product
  const openEditPriceDialog = (product) => {
    setSelectedProductForEdit(product);
    setAddPriceForm({
      supplier_id: "",
      purchase_price: "",
      currency: "SEK",
      ean: product.ean,
      produktnamn: product.produktnamn || product.artikelnummer
    });
    setShowAddDialog(true);
  };

  // Save price for a supplier
  const handleSaveSupplierPrice = async (supplierId, priceValue, currency, existingPriceId = null) => {
    if (!priceValue || isNaN(parseFloat(priceValue))) {
      toast.error("Ange ett giltigt pris");
      return;
    }
    
    setSavingPrice(true);
    try {
      if (existingPriceId) {
        // Update existing price
        await axios.put(`${API}/purchasing/prices/${existingPriceId}`, {
          purchase_price: parseFloat(priceValue),
          currency: currency
        });
      } else {
        // Create new price
        await axios.post(`${API}/purchasing/prices`, {
          supplier_id: supplierId,
          ean: selectedProductForEdit.ean,
          purchase_price: parseFloat(priceValue),
          currency: currency
        });
      }
      
      toast.success("Pris sparat");
      
      // Check if we need to return to orders page (from order flow)
      const returnData = sessionStorage.getItem('returnToOrder');
      if (returnData) {
        try {
          const data = JSON.parse(returnData);
          if (data.fromQuickOrder) {
            // Close dialog and navigate back to orders
            setShowAddDialog(false);
            setSelectedProductForEdit(null);
            toast.info("Navigerar tillbaka till beställningen...");
            navigate('/purchasing/orders');
            return;
          }
        } catch (e) {
          console.error("Error parsing return data:", e);
        }
      }
      
      fetchInitialData(); // Refresh data
    } catch (error) {
      toast.error("Kunde inte spara pris");
    } finally {
      setSavingPrice(false);
    }
  };

  const handleDeletePrice = async (priceId) => {
    try {
      await axios.delete(`${API}/purchasing/prices/${priceId}`);
      toast.success("Pris borttaget");
      fetchInitialData(); // Refresh data
      
      // Update the selected product's prices
      if (selectedProductForEdit) {
        const updatedProduct = { ...selectedProductForEdit };
        // Remove the deleted price from the prices object
        for (const supplierId in updatedProduct.prices) {
          if (updatedProduct.prices[supplierId]?.id === priceId) {
            delete updatedProduct.prices[supplierId];
            break;
          }
        }
        setSelectedProductForEdit(updatedProduct);
      }
    } catch (error) {
      toast.error("Kunde inte ta bort pris: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddPrice = async () => {
    const eanToUse = addPriceForm.ean;
    
    if (!eanToUse || !addPriceForm.supplier_id || !addPriceForm.purchase_price) {
      toast.error("Fyll i alla obligatoriska fält (leverantör, produkt/EAN, pris)");
      return;
    }
    
    setSavingPrice(true);
    try {
      // Parse chemical_tax properly - treat empty or undefined as 0, not null
      const chemicalTaxValue = addPriceForm.chemical_tax !== "" && addPriceForm.chemical_tax !== undefined 
        ? parseFloat(addPriceForm.chemical_tax) 
        : 0;
      
      await axios.post(`${API}/purchasing/prices`, {
        supplier_id: addPriceForm.supplier_id,
        ean: eanToUse,
        purchase_price: parseFloat(addPriceForm.purchase_price),
        currency: addPriceForm.currency,
        chemical_tax: isNaN(chemicalTaxValue) ? 0 : chemicalTaxValue
      });
      
      toast.success("Pris tillagt");
      setShowAddDialog(false);
      setSelectedProductForEdit(null);
      setAddPriceForm({ supplier_id: "", purchase_price: "", currency: "SEK", ean: "", produktnamn: "", chemical_tax: "0" });
      setProductSearchForAdd("");
      setProductSearchResults([]);
      
      // Check if we need to return to orders page
      const returnData = sessionStorage.getItem('returnToOrder');
      if (returnData) {
        try {
          const data = JSON.parse(returnData);
          if (data.fromQuickOrder) {
            // Keep return data for orders page to pick up
            toast.info("Navigerar tillbaka till beställningen...");
            navigate('/purchasing/orders');
            return;
          }
        } catch (e) {
          console.error("Error parsing return data:", e);
        }
      }
      
      // Refresh data
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte lägga till pris");
    } finally {
      setSavingPrice(false);
    }
  };
  
  const openAddPriceDialog = async (prefilledEan = null, prefilledName = null) => {
    // Check if product already has chemical_tax
    let existingChemicalTax = "";
    if (prefilledEan) {
      try {
        const response = await axios.get(`${API}/purchasing/prices/by-ean/${prefilledEan}`);
        const prices = response.data || [];
        const priceWithTax = prices.find(p => p.chemical_tax);
        if (priceWithTax) {
          existingChemicalTax = priceWithTax.chemical_tax.toString();
        }
      } catch (error) {
        // Ignore errors
      }
    }
    
    setAddPriceForm({
      supplier_id: "",
      purchase_price: "",
      currency: "SEK",
      ean: prefilledEan || "",
      produktnamn: prefilledName || "",
      chemical_tax: existingChemicalTax
    });
    setProductSearchForAdd("");
    setProductSearchResults([]);
    setShowAddDialog(true);
  };
  
  const selectProductForAdd = async (product) => {
    // Check if product already has chemical_tax
    let existingChemicalTax = "";
    try {
      const response = await axios.get(`${API}/purchasing/prices/by-ean/${product.ean}`);
      const prices = response.data || [];
      const priceWithTax = prices.find(p => p.chemical_tax);
      if (priceWithTax) {
        existingChemicalTax = priceWithTax.chemical_tax.toString();
      }
    } catch (error) {
      // Ignore errors
    }
    
    setAddPriceForm(prev => ({
      ...prev,
      ean: product.ean,
      produktnamn: product.produktnamn,
      supplier_id: suppliers[0]?.id || "",
      chemical_tax: existingChemicalTax
    }));
    setProductSearchForAdd(product.artikelnummer || product.ean);
    setProductSearchResults([]);
  };

  const openCreateProductDialog = () => {
    // Pre-fill with search query if it looks like an EAN or artikelnummer
    const query = searchQuery || productSearchForAdd;
    const isEan = /^\d{8,13}$/.test(query);
    
    setCreateProductForm({
      artikelnummer: isEan ? "" : query,
      ean: isEan ? query : "",
      kategori: "",
      tillverkare: ""
    });
    setShowCreateProduct(true);
  };

  const handleCreateProduct = async () => {
    if (!createProductForm.artikelnummer || !createProductForm.ean) {
      toast.error("Artikelnummer och EAN är obligatoriska");
      return;
    }
    
    setCreatingProduct(true);
    try {
      await axios.post(`${API}/inventory`, createProductForm);
      toast.success(`Produkt ${createProductForm.artikelnummer} skapad!`);
      setShowCreateProduct(false);
      
      // Select the newly created product
      const newProduct = {
        ean: createProductForm.ean,
        artikelnummer: createProductForm.artikelnummer
      };
      
      // If we're in the add price dialog, select it there
      if (showAddDialog) {
        selectProductForAdd(newProduct);
      } else {
        // Otherwise select it in main view
        selectProduct(newProduct);
      }
      
      setCreateProductForm({ artikelnummer: "", ean: "", kategori: "", tillverkare: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa produkt");
    } finally {
      setCreatingProduct(false);
    }
  };

  // Open quick order dialog with pre-selected product
  // First checks if product is already ordered (fast endpoint)
  const handleOrderProduct = async (product) => {
    try {
      // Fast check using dedicated endpoint
      const response = await axios.get(`${API}/purchasing/check-ordered/${product.ean}`);
      
      if (response.data.is_ordered) {
        // Show warning dialog
        setOrderWarningProduct(product);
        setExistingOrder({
          orderId: response.data.order_id,
          orderNumber: response.data.order_number,
          supplierName: response.data.supplier_name,
          status: response.data.status,
          quantity: response.data.quantity,
          receivedQuantity: response.data.received_quantity
        });
        setShowOrderWarning(true);
      } else {
        // Open quick order dialog directly
        openQuickOrder(product);
      }
    } catch (error) {
      console.error("Error checking orders:", error);
      // Open quick order dialog anyway if check fails
      openQuickOrder(product);
    }
  };
  
  const openQuickOrder = (product) => {
    // Get the best price info for this product
    const pricesWithSuppliers = suppliers
      .map(supplier => {
        const priceInfo = product.prices[supplier.id];
        if (!priceInfo) return null;
        return {
          supplierId: supplier.id,
          supplierName: supplier.company_name,
          price: priceInfo.purchase_price,
          currency: priceInfo.currency || "SEK"
        };
      })
      .filter(Boolean);
    
    const bestPrice = pricesWithSuppliers.length > 0 ? pricesWithSuppliers[0] : null;
    
    setQuickOrderProduct({
      ean: product.ean,
      artikelnummer: product.artikelnummer,
      produktnamn: product.produktnamn,
      supplierId: bestPrice?.supplierId,
      supplierName: bestPrice?.supplierName,
      price: bestPrice?.price,
      currency: bestPrice?.currency
    });
    setShowQuickOrderDialog(true);
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/purchasing">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Prislistor
                </h1>
                <p className="text-sm text-muted-foreground">
                  {allProducts.length} produkter med inköpspriser
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                onClick={() => setShowImportDialog(true)}
                data-testid="import-prices-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Importera
              </Button>
              <Button 
                onClick={() => openAddPriceDialog()}
                data-testid="add-price-main-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Lägg till pris
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-4">

        {/* Search and Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Sök produkt (artikelnummer, EAN)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="price-search"
            />
          </div>
          
          {/* Brand filter - Searchable */}
          <Popover open={brandFilterOpen} onOpenChange={setBrandFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-48 justify-between">
                {filterBrand || "Alla märken"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-0">
              <Command>
                <CommandInput placeholder="Sök märke..." />
                <CommandList>
                  <CommandEmpty>Inget märke hittades</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value=""
                      onSelect={() => { setFilterBrand(""); setCurrentPage(0); setBrandFilterOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", !filterBrand ? "opacity-100" : "opacity-0")} />
                      Alla märken
                    </CommandItem>
                    {availableBrands.map(brand => (
                      <CommandItem
                        key={brand}
                        value={brand}
                        onSelect={() => { setFilterBrand(brand); setCurrentPage(0); setBrandFilterOpen(false); }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", filterBrand === brand ? "opacity-100" : "opacity-0")} />
                        {brand}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          
          {/* Category filter - Searchable */}
          <Popover open={categoryFilterOpen} onOpenChange={setCategoryFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-48 justify-between">
                {filterCategory || "Alla kategorier"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-0">
              <Command>
                <CommandInput placeholder="Sök kategori..." />
                <CommandList>
                  <CommandEmpty>Ingen kategori hittades</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value=""
                      onSelect={() => { setFilterCategory(""); setCurrentPage(0); setCategoryFilterOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", !filterCategory ? "opacity-100" : "opacity-0")} />
                      Alla kategorier
                    </CommandItem>
                    {availableCategories.map(cat => (
                      <CommandItem
                        key={cat}
                        value={cat}
                        onSelect={() => { setFilterCategory(cat); setCurrentPage(0); setCategoryFilterOpen(false); }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", filterCategory === cat ? "opacity-100" : "opacity-0")} />
                        {cat}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Supplier filter */}
          <Popover open={supplierFilterOpen} onOpenChange={setSupplierFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-48 justify-between">
                {filterSupplier ? (availableSuppliers.find(s => s.id === filterSupplier)?.company_name || filterSupplier) : "Alla leverantörer"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-0">
              <Command>
                <CommandInput placeholder="Sök leverantör..." />
                <CommandList>
                  <CommandEmpty>Ingen leverantör hittades</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value=""
                      onSelect={() => { setFilterSupplier(""); setCurrentPage(0); setSupplierFilterOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", !filterSupplier ? "opacity-100" : "opacity-0")} />
                      Alla leverantörer
                    </CommandItem>
                    {availableSuppliers.map(s => (
                      <CommandItem
                        key={s.id}
                        value={s.company_name}
                        onSelect={() => { setFilterSupplier(s.id); setCurrentPage(0); setSupplierFilterOpen(false); }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", filterSupplier === s.id ? "opacity-100" : "opacity-0")} />
                        {s.company_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          
          {/* Active filters indicator */}
          {(filterBrand || filterCategory || filterSupplier) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setFilterBrand(""); setFilterCategory(""); setFilterSupplier(""); setCurrentPage(0); }}
              className="text-muted-foreground"
            >
              <X className="h-4 w-4 mr-1" />
              Rensa filter
            </Button>
          )}
        </div>
        
        {/* Result count */}
        <div className="text-sm text-muted-foreground">
          Visar {filteredProducts.length} av {totalProducts} produkter
          {(filterBrand || filterCategory || filterSupplier || searchQuery) && " (filtrerat)"}
        </div>

        {/* Export button */}
        <Button 
          variant="outline" 
          size="sm"
          onClick={async () => {
            try {
              toast.info("Hämtar alla produkter...");
              // Fetch ALL products (no pagination) for export
              const params = { limit: 10000, skip: 0 };
              if (searchQuery) params.search = searchQuery;
              if (filterBrand) params.brand = filterBrand;
              if (filterCategory) params.category = filterCategory;
              if (filterSupplier) params.supplier = filterSupplier;
              const res = await axios.get(`${API}/purchasing/prices/all-products`, { params, headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }});
              const allExportProducts = res.data.products || [];
              
              const rows = [["Artikelnummer", "EAN", "Produktnamn", "Tillverkare", "Kategori", "Kemskatt", "Frakt", ...suppliers.map(s => `${s.company_name} (Pris)`), ...suppliers.map(s => `${s.company_name} (Valuta)`), "Totalpris (SEK)"]];
              allExportProducts.forEach(p => {
                const lowestSEK = Object.values(p.prices || {}).reduce((min, pr) => {
                  const sek = pr.currency === 'EUR' ? pr.purchase_price * exchangeRate : pr.purchase_price;
                  return sek > 0 && sek < min ? sek : min;
                }, Infinity);
                const chemTax = Object.values(p.prices || {}).find(pr => pr?.chemical_tax)?.chemical_tax || 0;
                const total = (lowestSEK < Infinity ? lowestSEK : 0) + chemTax + (p.freight_cost || 0);
                rows.push([
                  p.artikelnummer, p.ean, p.produktnamn, p.tillverkare, p.kategori,
                  chemTax, p.freight_cost || 0,
                  ...suppliers.map(s => p.prices?.[s.id]?.purchase_price || ""),
                  ...suppliers.map(s => p.prices?.[s.id]?.currency || ""),
                  total > 0 ? Math.round(total) : ""
                ]);
              });
              const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
              const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `prislistor_${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success(`${allExportProducts.length} produkter exporterade`);
            } catch (err) {
              toast.error("Kunde inte exportera");
            }
          }}
          data-testid="export-prices-btn"
        >
          <Download className="h-4 w-4 mr-1" />
          Exportera CSV
        </Button>

        {/* Products Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading text-lg mb-2">Inga priser registrerade</h3>
              <p className="text-muted-foreground mb-4">
                Lägg till inköpspriser för att se dem här
              </p>
              <Button onClick={() => openAddPriceDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Lägg till pris
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              {/* Table Header */}
              <div className="grid grid-cols-[180px_140px_80px_80px_1fr_90px_100px_100px] gap-4 p-3 border-b bg-muted/50 text-sm font-medium">
                <div>Artikel</div>
                <div>EAN</div>
                <div>Kem. skatt</div>
                <div>Frakt</div>
                <div>Inköpspris</div>
                <div>Totalpris</div>
                <div></div>
                <div></div>
              </div>
              
              {/* Table Body */}
              <div className="divide-y">
                {filteredProducts.map(product => (
                  <div 
                    key={product.ean}
                    className="grid grid-cols-[180px_140px_80px_80px_1fr_90px_100px_100px] gap-4 p-3 items-center hover:bg-muted/30"
                  >
                    <div>
                      <Link to={`/inventory/${product.ean}`} className="font-mono font-medium text-primary hover:underline">
                        {product.artikelnummer}
                      </Link>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {product.ean}
                    </div>
                    <div>
                      {/* Kem. skatt - editable inline */}
                      <ChemicalTaxCell 
                        product={product}
                        onSave={(newTax) => handleSaveChemicalTax(product.ean, newTax)}
                      />
                    </div>
                    <div className="text-sm">
                      {/* Frakt - from last delivery */}
                      {product.freight_cost > 0 ? (
                        <span className="text-blue-500 font-medium">{product.freight_cost.toFixed(0)} kr</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                    <div>
                      {/* Compact price display with expand */}
                      <CompactPriceDisplay
                        product={product}
                        suppliers={suppliers}
                        exchangeRate={exchangeRate}
                        onEditPrice={openEditPriceDialog}
                      />
                    </div>
                    <div className="text-sm">
                      {/* Totalpris = inköpspris + kemskatt + frakt */}
                      {(() => {
                        const prices = Object.values(product.prices || {}).filter(p => p?.purchase_price > 0);
                        const lowestPriceSEK = prices.length > 0
                          ? Math.min(...prices.map(p => p.currency === 'EUR' ? p.purchase_price * exchangeRate : p.purchase_price))
                          : 0;
                        const chemTax = Object.values(product.prices || {}).find(p => p?.chemical_tax)?.chemical_tax || 0;
                        const freight = product.freight_cost || 0;
                        const total = lowestPriceSEK + chemTax + freight;
                        return total > 0 ? (
                          <span className="font-bold text-emerald-500">{Math.round(total).toLocaleString('sv-SE')} kr</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        );
                      })()}
                    </div>
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => handleOrderProduct(product)}
                        data-testid={`order-btn-${product.ean}`}
                      >
                        <ShoppingCart className="h-3 w-3" />
                        Beställ
                      </Button>
                    </div>
                    <div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => openEditPriceDialog(product)}
                        data-testid={`edit-btn-${product.ean}`}
                      >
                        <Edit2 className="h-3 w-3" />
                        Redigera
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Sida {currentPage + 1} av {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Föregående
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Nästa
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Add/Edit Price Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setSelectedProductForEdit(null);
          setAddPriceForm({ supplier_id: "", purchase_price: "", currency: "SEK", ean: "", produktnamn: "", chemical_tax: "0" });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {selectedProductForEdit ? "Redigera priser" : "Lägg till pris"}
            </DialogTitle>
            <DialogDescription>
              {selectedProductForEdit 
                ? `Priser för ${selectedProductForEdit.artikelnummer}` 
                : "Sök efter en produkt och välj leverantör"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* If editing existing product, show all suppliers with their prices */}
            {selectedProductForEdit ? (
              <div className="space-y-3">
                {/* Suppliers WITH prices - show first */}
                {suppliers
                  .filter(s => selectedProductForEdit.prices?.[s.id])
                  .map(supplier => (
                    <SupplierPriceRow
                      key={supplier.id}
                      supplier={supplier}
                      existingPrice={selectedProductForEdit.prices[supplier.id]}
                      onSave={(price, currency) => handleSaveSupplierPrice(supplier.id, price, currency, selectedProductForEdit.prices[supplier.id]?.id)}
                      onDelete={handleDeletePrice}
                      savingPrice={savingPrice}
                      exchangeRate={exchangeRate}
                    />
                  ))}
                
                {/* Add new supplier price */}
                {(() => {
                  const suppliersWithoutPrice = suppliers.filter(s => !selectedProductForEdit.prices?.[s.id]);
                  if (suppliersWithoutPrice.length === 0) return null;
                  return (
                    <div className="border border-dashed rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-2">Lägg till pris från ny leverantör</p>
                      <div className="flex gap-2 flex-wrap">
                        {suppliersWithoutPrice.map(s => (
                          <Button
                            key={s.id}
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => {
                              // Add empty price for this supplier so SupplierPriceRow appears
                              setSelectedProductForEdit(prev => ({
                                ...prev,
                                prices: {
                                  ...prev.prices,
                                  [s.id]: { purchase_price: 0, currency: s.default_currency || "SEK", id: null }
                                }
                              }));
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {s.company_name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              /* Product search for adding new product */
              <div className="space-y-2">
                <Label>Produkt *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Sök produkt (EAN eller artikelnummer)..."
                    value={productSearchForAdd}
                    onChange={(e) => setProductSearchForAdd(e.target.value)}
                    className="pl-10"
                    data-testid="add-price-product-search"
                  />
                  {searchingForAdd && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
                  )}
                </div>
                
                {/* Search results dropdown */}
                {productSearchResults.length > 0 && !addPriceForm.ean && (
                  <div className="border rounded-md max-h-40 overflow-y-auto">
                    {productSearchResults.map(product => (
                      <div
                        key={product.ean}
                        className="p-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                        onClick={() => selectProductForAdd(product)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-sm">{product.artikelnummer}</span>
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">{product.ean}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* No results in dialog - offer to create */}
                {productSearchForAdd.length >= 2 && productSearchResults.length === 0 && !searchingForAdd && !addPriceForm.ean && (
                  <div className="p-3 bg-muted/50 rounded-md flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Ingen produkt hittades</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={openCreateProductDialog}
                      className="gap-1"
                    >
                      <PackagePlus className="h-3 w-3" />
                      Skapa ny
                    </Button>
                  </div>
                )}
                
                {/* Selected product display */}
                {addPriceForm.ean && (
                  <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-mono font-medium text-sm">{productSearchForAdd}</p>
                      <p className="text-xs text-muted-foreground font-mono">{addPriceForm.ean}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setAddPriceForm(prev => ({ ...prev, ean: "", produktnamn: "" }));
                        setProductSearchForAdd("");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Only show supplier/price form when adding new (not editing existing) */}
            {!selectedProductForEdit && addPriceForm.ean && (
              <>
                {/* Supplier selection */}
                <div className="space-y-2">
                  <Label>Leverantör *</Label>
                  <Select 
                    value={addPriceForm.supplier_id} 
                    onValueChange={(v) => {
                      const supplier = suppliers.find(s => s.id === v);
                      const defaultCurrency = supplier?.default_currency || "SEK";
                      setAddPriceForm(prev => ({ 
                        ...prev, 
                        supplier_id: v,
                        currency: defaultCurrency
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="add-price-supplier-select">
                      <SelectValue placeholder="Välj leverantör..." />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => {
                        const foundProduct = allProducts.find(p => p.ean === addPriceForm.ean);
                        const hasPrice = foundProduct?.prices?.[s.id];
                        return (
                          <SelectItem key={s.id} value={s.id}>
                            {s.company_name}
                            {s.default_currency && s.default_currency !== "SEK" && ` (${s.default_currency})`}
                            {hasPrice && " - har redan pris"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Inköpspris *</Label>
                    <Input
                      type="number"
                      step="1"
                      placeholder="0"
                      value={addPriceForm.purchase_price}
                      onChange={(e) => setAddPriceForm(prev => ({ ...prev, purchase_price: e.target.value }))}
                      data-testid="add-price-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Valuta</Label>
                    <Select 
                      value={addPriceForm.currency}
                      onValueChange={(v) => setAddPriceForm(prev => ({ ...prev, currency: v }))}
                    >
                      <SelectTrigger data-testid="add-price-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SEK">SEK</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Chemical Tax */}
                <div className="space-y-2">
                  <Label>Kem. skatt (SEK per st)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    value={addPriceForm.chemical_tax}
                    onChange={(e) => setAddPriceForm(prev => ({ ...prev, chemical_tax: e.target.value }))}
                    data-testid="add-chemical-tax-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Kemikalieskatt per styck (valfritt)
                  </p>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            {selectedProductForEdit ? (
              <Button onClick={() => setShowAddDialog(false)}>
                Stäng
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Avbryt
                </Button>
                <Button 
                  onClick={handleAddPrice} 
                  disabled={savingPrice || !addPriceForm.ean || !addPriceForm.supplier_id}
                  data-testid="save-price-btn"
                >
                  {savingPrice ? "Sparar..." : "Spara pris"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Product Dialog */}
      <Dialog open={showCreateProduct} onOpenChange={setShowCreateProduct}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5 text-green-500" />
              Skapa ny produkt
            </DialogTitle>
            <DialogDescription>
              Fyll i produktuppgifterna för att skapa en ny produkt i systemet
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Artikelnummer *</Label>
                <Input
                  placeholder="T.ex. ABC-123"
                  value={createProductForm.artikelnummer}
                  onChange={(e) => setCreateProductForm(prev => ({ ...prev, artikelnummer: e.target.value }))}
                  data-testid="create-product-artikelnummer"
                />
              </div>
              <div className="space-y-2">
                <Label>EAN *</Label>
                <Input
                  placeholder="8-13 siffror"
                  value={createProductForm.ean}
                  onChange={(e) => setCreateProductForm(prev => ({ ...prev, ean: e.target.value }))}
                  data-testid="create-product-ean"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={categoryOpen}
                      className="w-full justify-between font-normal"
                    >
                      {createProductForm.kategori || "Välj kategori..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök kategori..." />
                      <CommandList className="max-h-48">
                        <CommandEmpty>Ingen kategori hittad</CommandEmpty>
                        <CommandGroup>
                          {categories.map((cat) => (
                            <CommandItem
                              key={cat}
                              value={cat}
                              onSelect={() => {
                                setCreateProductForm(prev => ({ ...prev, kategori: cat }));
                                setCategoryOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", createProductForm.kategori === cat ? "opacity-100" : "opacity-0")} />
                              {cat}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Tillverkare</Label>
                <Popover open={manufacturerOpen} onOpenChange={setManufacturerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={manufacturerOpen}
                      className="w-full justify-between font-normal"
                    >
                      {createProductForm.tillverkare || "Välj tillverkare..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök tillverkare..." />
                      <CommandList className="max-h-48">
                        <CommandEmpty>Ingen tillverkare hittad</CommandEmpty>
                        <CommandGroup>
                          {manufacturers.map((man) => (
                            <CommandItem
                              key={man}
                              value={man}
                              onSelect={() => {
                                setCreateProductForm(prev => ({ ...prev, tillverkare: man }));
                                setManufacturerOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", createProductForm.tillverkare === man ? "opacity-100" : "opacity-0")} />
                              {man}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateProduct(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleCreateProduct} 
              disabled={creatingProduct || !createProductForm.artikelnummer || !createProductForm.ean}
              className="gap-2"
            >
              {creatingProduct ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Skapar...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Skapa produkt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Prices Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportFile(null);
          setImportResult(null);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-500" />
              Importera prislistor
            </DialogTitle>
            <DialogDescription>
              Importera priser från CSV-fil för befintliga produkter
            </DialogDescription>
          </DialogHeader>
          
          {!importResult ? (
            <div className="space-y-4">
              {/* Match by */}
              <div className="space-y-2">
                <Label>Matcha produkter via</Label>
                <Select value={importMatchBy} onValueChange={setImportMatchBy}>
                  <SelectTrigger data-testid="import-match-by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ean">EAN</SelectItem>
                    <SelectItem value="artikelnummer">Artikelnummer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Default supplier */}
              <div className="space-y-2">
                <Label>Standardleverantör (om ej i CSV)</Label>
                <Select value={importSupplierId} onValueChange={setImportSupplierId}>
                  <SelectTrigger data-testid="import-supplier">
                    <SelectValue placeholder="Välj leverantör..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* File upload */}
              <div className="space-y-2">
                <Label>CSV-fil *</Label>
                <div 
                  className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => importFileRef.current?.click()}
                >
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileSpreadsheet className="h-5 w-5 text-green-500" />
                      <span className="font-medium">{importFile.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImportFile(null);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-6 w-6 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Klicka för att välja CSV-fil
                      </p>
                    </div>
                  )}
                </div>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  data-testid="import-file-input"
                />
              </div>
              
              {/* CSV format info */}
              <div className="p-3 bg-muted/50 rounded-lg text-sm space-y-2">
                <p className="font-medium">CSV-format:</p>
                <div className="text-muted-foreground space-y-1">
                  <p><strong>Obligatorisk kolumn:</strong></p>
                  <p className="pl-2">• <code className="text-xs bg-muted px-1 rounded">ean</code> eller <code className="text-xs bg-muted px-1 rounded">artikelnummer</code> (för matchning)</p>
                  
                  <p className="mt-2"><strong>Valfria kolumner:</strong></p>
                  <p className="pl-2">• <code className="text-xs bg-muted px-1 rounded">pris</code> - Inköpspris (kräver leverantör)</p>
                  <p className="pl-2">• <code className="text-xs bg-muted px-1 rounded">leverantör</code> - Leverantörens namn</p>
                  <p className="pl-2">• <code className="text-xs bg-muted px-1 rounded">valuta</code> - SEK eller EUR</p>
                  <p className="pl-2">• <code className="text-xs bg-muted px-1 rounded">kem_skatt</code> - Kemisk skatt per enhet</p>
                </div>
                <p className="text-xs text-muted-foreground/70 mt-2 border-t border-muted pt-2">
                  Tips: Du kan importera bara kem. skatt utan pris - den uppdateras på alla befintliga prislistor för produkten.
                </p>
              </div>
            </div>
          ) : (
            /* Import result */
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-3">
                  {importResult.summary.errors === 0 ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                  <span className="font-medium">Import klar</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Totalt rader:</span>
                    <span className="ml-2 font-medium">{importResult.summary.total_rows}</span>
                  </div>
                  <div>
                    <span className="text-green-500">Uppdaterade:</span>
                    <span className="ml-2 font-medium">{importResult.summary.updated}</span>
                  </div>
                  <div>
                    <span className="text-blue-500">Nya:</span>
                    <span className="ml-2 font-medium">{importResult.summary.created}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Överhoppade:</span>
                    <span className="ml-2 font-medium">{importResult.summary.skipped}</span>
                  </div>
                  {importResult.summary.errors > 0 && (
                    <div>
                      <span className="text-red-500">Fel:</span>
                      <span className="ml-2 font-medium">{importResult.summary.errors}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Skipped items */}
              {importResult.skipped?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Överhoppade ({importResult.summary.skipped})
                  </Label>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importResult.skipped.slice(0, 10).map((item, i) => (
                      <div key={i} className="text-xs p-2 bg-muted/30 rounded flex justify-between">
                        <span className="font-mono">{item.match_value || `Rad ${item.row}`}</span>
                        <span className="text-muted-foreground">{item.reason}</span>
                      </div>
                    ))}
                    {importResult.summary.skipped > 10 && (
                      <p className="text-xs text-muted-foreground text-center">
                        ... och {importResult.summary.skipped - 10} till
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {!importResult ? (
              <>
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                  Avbryt
                </Button>
                <Button 
                  onClick={async () => {
                    if (!importFile) {
                      toast.error("Välj en CSV-fil");
                      return;
                    }
                    
                    setIsImporting(true);
                    try {
                      const formData = new FormData();
                      formData.append("file", importFile);
                      formData.append("match_by", importMatchBy);
                      if (importSupplierId) formData.append("supplier_id", importSupplierId);
                      
                      const response = await axios.post(`${API}/purchasing/prices/import`, formData);
                      setImportResult(response.data);
                      toast.success(response.data.message);
                      fetchInitialData(); // Refresh data
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Import misslyckades");
                    } finally {
                      setIsImporting(false);
                    }
                  }}
                  disabled={isImporting || !importFile}
                  data-testid="import-submit-btn"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importerar...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importera
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => {
                setShowImportDialog(false);
                setImportResult(null);
                setImportFile(null);
              }}>
                Stäng
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Warning Dialog - Product already ordered */}
      <Dialog open={showOrderWarning} onOpenChange={setShowOrderWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertCircle className="h-5 w-5" />
              Produkten är redan beställd
            </DialogTitle>
            <DialogDescription>
              {orderWarningProduct && existingOrder && (
                <div className="mt-4 space-y-3">
                  <p>
                    <strong>{orderWarningProduct.artikelnummer}</strong> finns redan i en aktiv beställning.
                  </p>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Order:</span>
                      <span className="font-medium">{existingOrder.orderNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Leverantör:</span>
                      <span>{existingOrder.supplierName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Antal:</span>
                      <span>{existingOrder.quantity} st</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge variant={existingOrder.status === 'ordered' ? 'default' : 'secondary'}>
                        {existingOrder.status === 'pending' ? 'Ska beställas' : 
                         existingOrder.status === 'ordered' ? 'Beställd' : 
                         existingOrder.status === 'in_transit' ? 'På väg' : existingOrder.status}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    Vill du skapa en ny beställning ändå, eller gå till den befintliga?
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowOrderWarning(false);
                navigate("/purchasing/orders");
              }}
            >
              Gå till beställningar
            </Button>
            <Button
              onClick={() => {
                setShowOrderWarning(false);
                if (orderWarningProduct) {
                  openQuickOrder(orderWarningProduct);
                }
              }}
            >
              Beställ ändå
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Order Dialog */}
      <QuickOrderDialog
        open={showQuickOrderDialog}
        onOpenChange={setShowQuickOrderDialog}
        suppliers={suppliers}
        exchangeRate={exchangeRate}
        preselectedProduct={quickOrderProduct}
        onOrderCreated={() => {
          setQuickOrderProduct(null);
          fetchProducts();
        }}
        navigateToOrdersAfter={true}
      />
    </div>
  );
}
